import type {ProductTourEventDto} from '@getmoss/api-gateway'
import {track} from '@getmoss/moss-analytics'
import {useCallback, useMemo} from 'react'
import {useGetDomainCountry} from 'src/common/hooks/useGetDomainCountry'
import {useMossApi} from 'src/common/hooks/useMossApi'
import {useLeadIdContext} from '../context/LeadIdContext'

export type ProductTourAnalyticsEvents = {
  startTour: (buttonLabel: string) => void
  selectTour: (tourIndex: number, tourName: string) => void
  submitLead: (leadId: string) => void
  error: (errorType: string, details?: string) => void
  demoStepCounter: (demoName: string, stepNumber: number, chapterName: string) => void
  onDemoLoadError: (demoName: string) => void
  chapterStarted: (demoName: string, chapterName: string, productName: string) => void
  chapterFinished: (demoName: string, chapterName: string, productName: string) => void
  handleDemoEventApi: (event: ProductTourEventDto['productTourEvent']) => void
  handleConvertCtaClick: (payload: Record<string, unknown>) => void
  demoButtonClick: (product: string) => void
  getStartedButtonClick: (product: string) => void
}

export const useProductTourAnalytics = (pageUid: string): ProductTourAnalyticsEvents => {
  const domainCountry = useGetDomainCountry()
  const {mossApi} = useMossApi({})
  const {leadId} = useLeadIdContext()

  const basePageData = useMemo(
    () => ({
      type: pageUid || '',
      domainCountry,
    }),
    [pageUid, domainCountry],
  )

  const handleDemoEventApi = useCallback(
    (event: ProductTourEventDto['productTourEvent']) => {
      if (!leadId) {
        return
      }

      mossApi.leadController.saveProductTourEvent(leadId, {
        productTourEvent: event,
      })
    },
    [mossApi, leadId],
  )

  const demoStepCounter = useCallback(
    (demoName: string, stepNumber: number, chapterName: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: `demo-step-${stepNumber}-button`,
          location: `demo-${demoName}`,
          type: 'click',
          detail: {
            chapter: chapterName,
            leadId,
          },
        },
        page: {
          type: 'product-tour-demo',
          domainCountry: domainCountry,
        },
      })
    },
    [domainCountry, leadId],
  )

  const onDemoLoadError = useCallback(
    (demoName: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: 'iframe',
          elementText: demoName,
          location: 'product-tour-section',
          type: 'error',
          detail: 'iframe failed to load',
        },
        page: {
          type: 'product-tour-section',
          domainCountry,
        },
      })
    },
    [domainCountry],
  )

  const chapterFinished = useCallback(
    (demoName: string, chapterName: string, productName: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: `demo-${demoName}-chapter-${chapterName}-finished`,
          location: `product-tour-${productName}`,
          type: 'click',
          detail: {
            leadId,
          },
        },
        page: {
          type: `demo-${demoName}-chapter-${chapterName}-finished`,
          domainCountry: domainCountry,
        },
      })
    },
    [domainCountry, leadId],
  )

  const chapterStarted = useCallback(
    (demoName: string, chapterName: string, productName: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: `demo-${demoName}-chapter-${chapterName}-started`,
          location: `product-tour-${productName}`,
          type: 'click',
          detail: {
            leadId,
          },
        },
        page: {
          type: `demo-${demoName}-chapter-${chapterName}-started`,
          domainCountry: domainCountry,
        },
      })
    },
    [domainCountry, leadId],
  )

  const startTour = useCallback(
    (buttonLabel: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: 'product-tour-start-tour-button',
          location: 'product-tour-section',
          elementText: buttonLabel,
          type: 'click',
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const selectTour = useCallback(
    (tourIndex: number, tourName: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: 'product-tour-selector',
          location: 'product-tour-section',
          elementText: tourName,
          type: 'click',
          detail: `tour-${tourIndex}`,
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const submitLead = useCallback(
    (leadIdParam: string) => {
      track({
        event: 'form_submission',
        form: {
          formName: 'product-tour-lead-form',
          funnelId: leadIdParam,
          intent: 'product-tour',
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const error = useCallback(
    (errorType: string, details?: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: errorType,
          location: 'product-tour-section',
          type: 'error',
          detail: details,
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const handleConvertCtaClick = useCallback(
    (payload: Record<string, unknown>) => {
      track({
        event: 'interaction',
        interaction: {
          element: 'demo-button-clicked',
          location: 'product-demo',
          type: 'click',
          detail: JSON.stringify(payload),
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const demoButtonClick = useCallback(
    (product: string) => {
      track({
        event: 'interaction',
        interaction: {
          element: 'get-the-demo-button',
          location: 'product-tour-section',
          type: 'click',
          detail: JSON.stringify({product}),
        },
        page: basePageData,
      })
    },
    [basePageData],
  )

  const getStartedButtonClick = useCallback(() => {
    track({
      event: 'interaction',
      interaction: {
        element: 'get-started-button',
        location: 'product-tour-section',
        type: 'click',
      },
      page: basePageData,
    })
  }, [basePageData])

  return {
    startTour,
    selectTour,
    submitLead,
    error,
    onDemoLoadError,
    demoStepCounter,
    chapterFinished,
    chapterStarted,
    handleDemoEventApi,
    handleConvertCtaClick,
    demoButtonClick,
    getStartedButtonClick,
  }
}
